import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PaymentsIcon from "@mui/icons-material/Payments";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import PlaceIcon from "@mui/icons-material/Place";
import FlagIcon from "@mui/icons-material/Flag";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InsightsIcon from "@mui/icons-material/Insights";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventSeatIcon from "@mui/icons-material/EventSeat";

import { useDashboardWithPresentation } from "../hooks/useDashboardWithPresentation";
import { useDashboard } from "../hooks/useDashboard";
import { useDashboardTabState } from "../hooks/useDashboardTabState";
import { DashboardTabChrome } from "./dashboard/DashboardTabChrome";
import { Link as RouterLink } from "react-router-dom";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { getDashboardMetricInsights } from "../api/client";

/** Mesma família cromática da aba Vendas (VendaProdutos) */
const COLORS = {
  primary: "#0B60B0",
  accent: "#00B3C6",
  warm: "#FFB300",
  warn: "#FF7043",
  purple: "#7E57C2",
  teal: "#00897B",
  indigo: "#5C6BC0",
  green: "#43A047",
  pink: "#EC407A",
};

const KPI_MIN_H = 132;
const CARD_STRETCH = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: KPI_MIN_H,
};

/** Placeholders de analytics de tráfego */
const ANALYTICS_KPI_COLOR = "#546E7A";

const BRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Cabeçalho e container no mesmo padrão visual do DataGrid (VendaProdutos / Produtos). */
function useDashboardTableChrome() {
  const theme = useTheme();
  const headerBg =
    theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : "#F5F7FA";
  const headerCellSx = {
    bgcolor: headerBg,
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: "text.primary",
    borderBottom: 1,
    borderColor: "divider",
    py: 1.25,
    lineHeight: 1.35,
  };
  const stickyHeaderCellSx = {
    ...headerCellSx,
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: headerBg,
  };
  const paperContainerSx = {
    borderRadius: 1,
    overflow: "hidden",
  };
  const bodyRowHoverSx = {
    transition: "background-color 0.15s ease",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.05),
    },
  };
  const numCellSx = { fontVariantNumeric: "tabular-nums" };
  return { headerCellSx, stickyHeaderCellSx, paperContainerSx, bodyRowHoverSx, numCellSx };
}

function sourceSeriesChannelLabel(row, p) {
  const name = row?.channelName != null ? String(row.channelName).trim() : "";
  if (name) return name;
  const id = Number(row?.originId) || 0;
  if (id === 0) return p("channelNone");
  return p("channelUnknown", { id });
}

function formatTopTerminalLabel(name, qty, locale) {
  const n = name != null ? String(name).trim() : "";
  if (!n || n === "—") return "—";
  const q = Number(qty);
  if (!Number.isFinite(q) || q <= 0) return n;
  const loc = String(locale || "pt-BR").replace(/_/g, "-");
  return `${q.toLocaleString(loc)} · ${n}`;
}

/** Valor do KPI: quantidade claramente rotulada + nome do terminal (evita parecer ID). */
function TopTerminalKpiValue({ loading, name, qty, p, accent }) {
  if (loading) {
    return (
      <Typography variant="h4" sx={{ color: accent, fontWeight: 700, fontSize: { xs: "1.35rem", sm: "1.75rem" } }}>
        …
      </Typography>
    );
  }
  const n = name != null ? String(name).trim() : "";
  if (!n || n === "—") {
    return (
      <Typography variant="h4" sx={{ color: accent, fontWeight: 700, fontSize: { xs: "1.35rem", sm: "1.75rem" } }}>
        —
      </Typography>
    );
  }
  const q = Number(qty);
  const showQty = Number.isFinite(q) && q > 0;
  return (
    <Box>
      {showQty ? (
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontWeight: 700,
            color: "text.secondary",
            fontSize: "0.7rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            mb: 0.35,
            lineHeight: 1.2,
          }}
        >
          {p(q === 1 ? "kpiTopTerminalTicketCount_one" : "kpiTopTerminalTicketCount_other", { count: q })}
        </Typography>
      ) : null}
      <Typography
        variant="h5"
        component="p"
        title={n}
        sx={{
          color: accent,
          fontWeight: 700,
          fontSize: { xs: "0.92rem", sm: "1.06rem" },
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          m: 0,
        }}
      >
        {n}
      </Typography>
    </Box>
  );
}

/** >= 24 h → dias + horas restantes; abaixo disso só horas. */
function formatAvgLeadTimeDisplay(hours, p, i18nLang) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return null;
  const loc = String(i18nLang || "pt-BR").replace(/_/g, "-");
  const fmtDec = (n) =>
    n.toLocaleString(loc, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (h < 24) return p("avgLeadTime_hours", { hours: fmtDec(h) });
  const days = Math.floor(h / 24);
  const rem = h - days * 24;
  if (rem < 0.05) {
    return days === 1 ? p("avgLeadTime_1day") : p("avgLeadTime_ndays", { days });
  }
  if (days === 1) return p("avgLeadTime_1dayHours", { hours: fmtDec(rem) });
  return p("avgLeadTime_ndaysHours", { days, hours: fmtDec(rem) });
}

/** Eixo / tooltip: datas tipo YYYY-MM-DD → DD/MM/AAAA (pt-BR). */
function formatIsoDateAxisBr(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
  return s;
}

const KpiCard = ({ icon, title, value, loading, titleAction, color, helpText }) => {
  const accent = color || COLORS.primary;
  const valueIsPrimitive = typeof value === "string" || typeof value === "number";
  return (
    <Card
      variant="outlined"
      className="passagens-kpi-card"
      sx={{
        ...CARD_STRETCH,
        borderRadius: 2,
        borderColor: "divider",
        transition: (theme) =>
          theme.transitions.create(["box-shadow", "border-color"], { duration: 200 }),
        "&:hover": {
          boxShadow: 2,
          borderColor: "action.hover",
        },
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, flex: 1, py: 1.5, px: 2 }}>
        {loading ? (
          <Skeleton variant="circular" width={40} height={40} />
        ) : (
          <Avatar
            sx={{
              bgcolor: accent,
              width: 40,
              height: 40,
              color: "#fff",
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
            }}
          >
            {icon}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.25} sx={{ minHeight: 26 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}
              title={typeof title === "string" ? title : undefined}
            >
              {title}
            </Typography>
            {!loading && titleAction ? (
              <Box
                className="passagens-kpi-ai"
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0,
                  opacity: { xs: 1, sm: 0.45 },
                  transition: "opacity 0.2s",
                  ".passagens-kpi-card:hover &": { opacity: 1 },
                  "& .MuiIconButton-root": { p: 0.5 },
                }}
              >
                {titleAction}
              </Box>
            ) : null}
          </Stack>
          {loading ? (
            <Skeleton height={40} width="72%" sx={{ mt: 0.35 }} />
          ) : valueIsPrimitive ? (
            <Typography
              variant="h4"
              sx={{
                color: accent,
                fontWeight: 700,
                fontSize: { xs: "1.18rem", sm: "1.5rem" },
                lineHeight: 1.2,
                wordBreak: "break-word",
                mt: 0.2,
              }}
            >
              {value}
            </Typography>
          ) : (
            <Box sx={{ mt: 0.2 }}>{value}</Box>
          )}
          {!loading && helpText ? (
            <Typography
              component="p"
              variant="caption"
              sx={(theme) => ({
                mt: 0.75,
                mb: 0,
                color: theme.palette.text.secondary,
                opacity: 0.72,
                fontSize: "0.6875rem",
                fontWeight: 400,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              })}
            >
              {helpText}
            </Typography>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

const VendaPassagens = ({
  start,
  end,
  origens = [],
  destinos = [],
  ids_origens = [],
  status = "",
  presentationSnapshot = null,
  readOnly = false,
}) => {
  const { t, i18n } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const p = (key, opts) => t(`dashboardPage.passagens.${key}`, opts);
  const { headerCellSx, stickyHeaderCellSx, paperContainerSx, bodyRowHoverSx, numCellSx } =
    useDashboardTableChrome();

  const [geoFocus, setGeoFocus] = React.useState("origins");

  const params = React.useMemo(() => {
    const out = { start, end };
    if (Array.isArray(origens) && origens.length) out.origens = origens;
    if (Array.isArray(destinos) && destinos.length) out.destinos = destinos;
    if (Array.isArray(ids_origens) && ids_origens.length) out.ids_origens = ids_origens;
    if (status) out.status = status;
    return out;
  }, [start, end, origens, destinos, ids_origens, status]);
  const chartsQuery = useDashboardWithPresentation(
    "venda-passagens",
    "charts",
    params,
    { select: (payload) => payload || {} },
    presentationSnapshot
  );
  const { data, isLoading, error } = chartsQuery;
  const { data: soldTableData, isLoading: soldTableLoading } = useDashboard(
    "venda-passagens",
    "table",
    { ...params, limit: 50 },
    { enabled: !readOnly, preview: false, select: (payload) => payload || {} }
  );
  const { data: abandonedData, isLoading: abandonedLoading } = useDashboard(
    "venda-passagens",
    "abandonadas",
    { ...params, limit: 50, ttl_hours: 2 },
    { enabled: !readOnly, preview: false, select: (payload) => payload || {} }
  );

  const tab = useDashboardTabState(chartsQuery, {
    registerShare: !readOnly && !presentationSnapshot,
  });
  const loading = tab.showSkeletons;
  const meta = data?.meta || {};
  const cards = data?.cards || {};
  const charts = data?.charts || {};
  const origins = charts.origins || [];
  const destinations = charts.destinations || [];
  const topRoutes = charts.topRoutes || [];
  const dailySeries = charts.dailySeries || [];
  const weekdaySeries = charts.weekdaySeries || [];
  const hourSeries = charts.hourSeries || [];
  const sourceSeries = charts.sourceSeries || [];
  const funnel = charts.funnel || null;

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null);
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);
  const [contextInfoOpen, setContextInfoOpen] = React.useState(false);

  const passagensSummaryContext = React.useMemo(
    () => ({
      ticketsSold: Number(cards.ticketsSold || 0),
      orders: Number(cards.orders || 0),
      gmv: Number(cards.gmv || 0),
    }),
    [cards.ticketsSold, cards.orders, cards.gmv]
  );

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      return getDashboardMetricInsights("venda-passagens", {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: { start, end },
        summaryContext: { ...passagensSummaryContext, ...(cfg.summaryContext || {}) },
        filters: cfg.filters || {},
      });
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => (
    <Tooltip title="Explicar com IA">
      <IconButton size="small" onClick={() => openAi(cfg)} aria-label="Explicar com IA">
        <AutoAwesomeIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );

  const accent = geoFocus === "origins" ? "#0B60B0" : "#00B3C6";
  const hasAnyTickets =
    Number(cards.ticketsSold || 0) > 0 ||
    dailySeries.length > 0 ||
    origins.length > 0 ||
    destinations.length > 0;

  const weekdayLabel = (n) =>
    ({
      1: "Dom",
      2: "Seg",
      3: "Ter",
      4: "Qua",
      5: "Qui",
      6: "Sex",
      7: "Sáb",
    }[Number(n)] || String(n || ""));

  return (
    <Box>
      <DashboardTabChrome
        isDeferred={tab.isDeferred}
        isPreview={tab.isPreview}
        isInitialLoading={tab.showSkeletons}
        errorMessage={tab.errorMessage}
        emptyState={
          tab.showContent && !hasAnyTickets
            ? {
                visible: true,
                title: p("emptyStateTitle"),
                hint: p("emptyStateHint"),
              }
            : null
        }
      />

      <Dialog
        open={contextInfoOpen}
        onClose={() => setContextInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="passagens-context-info-title"
      >
        <DialogTitle id="passagens-context-info-title" sx={{ pr: 6 }}>
          {p("contextNotesTitle")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.08) : alpha("#0B60B0", 0.06),
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.65 }}>
                {p("sourceNote")}
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: "action.hover",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.65 }}>
                {p("funnelNote")}
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? alpha(theme.palette.warning.main, 0.12) : alpha("#FFB300", 0.1),
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.65 }}>
                {p("multiSeatNote")}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setContextInfoOpen(false)} disableElevation>
            {tCommon("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {meta?.trafficAnalyticsEnabled ? (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {p("analyticsPlaceholderTitle")}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
            {[
              {
                k: "newUsers",
                v: cards.newUsers,
                icon: <InsightsIcon />,
                metric: "passagens_analytics_new_users",
              },
              {
                k: "accesses",
                v: cards.accesses,
                icon: <InsightsIcon />,
                metric: "passagens_analytics_accesses",
              },
              {
                k: "retention",
                v: cards.retentionPct,
                icon: <InsightsIcon />,
                metric: "passagens_analytics_retention",
              },
              {
                k: "conversion",
                v: cards.conversionPct,
                icon: <InsightsIcon />,
                metric: "passagens_analytics_conversion",
              },
            ].map((row) => (
              <Grid item xs={6} sm={3} key={row.k} sx={{ display: "flex" }}>
                <KpiCard
                  icon={row.icon}
                  title={p(`kpi_${row.k}`)}
                  value={row.v != null ? String(row.v) : "—"}
                  color={ANALYTICS_KPI_COLOR}
                  helpText={row.v != null ? p(`kpi_${row.k}Help`) : p("analyticsPlaceholderSub")}
                  loading={loading}
                  titleAction={
                    <AiBtn
                      cfg={{
                        metric: row.metric,
                        metricName: p(`kpi_${row.k}`),
                        metricDefinition: p(`kpi_${row.k}Help`),
                        metricValue: row.v,
                        metricHuman: row.v != null ? String(row.v) : "—",
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </>
      ) : null}

      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant="subtitle1"
            component="h2"
            sx={{ fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.3 }}
          >
            {p("sectionSalesTitle")}
          </Typography>
          <Tooltip title={p("contextNotesTitle")}>
            <IconButton
              size="small"
              onClick={() => setContextInfoOpen(true)}
              aria-label={p("contextNotesTitle")}
              sx={{ color: "text.secondary", flexShrink: 0 }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0 }}>
          {p("sectionSalesSubtitle")}
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<ConfirmationNumberIcon />}
            title={p("kpiTickets")}
            value={loading ? "…" : String(cards.ticketsSold ?? 0)}
            color={COLORS.primary}
            helpText={p("kpiTicketsHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_tickets_sold",
                  metricName: p("kpiTickets"),
                  metricDefinition: p("kpiTicketsHelp"),
                  metricValue: Number(cards.ticketsSold ?? 0),
                  metricHuman: String(cards.ticketsSold ?? 0),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<ShoppingBagIcon />}
            title={p("kpiOrders")}
            value={loading ? "…" : String(cards.orders ?? 0)}
            color={COLORS.accent}
            helpText={p("kpiOrdersHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_orders",
                  metricName: p("kpiOrders"),
                  metricDefinition: p("kpiOrdersHelp"),
                  metricValue: Number(cards.orders ?? 0),
                  metricHuman: String(cards.orders ?? 0),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<PaymentsIcon />}
            title={p("kpiGmv")}
            value={loading ? "…" : BRL(cards.gmv)}
            color={COLORS.warm}
            helpText={p("kpiGmvHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_gmv",
                  metricName: p("kpiGmv"),
                  metricDefinition: p("kpiGmvHelp"),
                  metricValue: Number(cards.gmv ?? 0),
                  metricHuman: BRL(cards.gmv),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<TrendingFlatIcon />}
            title={p("kpiAvgTicket")}
            value={loading ? "…" : BRL(cards.avgTicket)}
            color={COLORS.warn}
            helpText={p("kpiAvgTicketHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_avg_ticket",
                  metricName: p("kpiAvgTicket"),
                  metricDefinition: p("kpiAvgTicketHelp"),
                  metricValue: Number(cards.avgTicket ?? 0),
                  metricHuman: BRL(cards.avgTicket),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<PlaceIcon />}
            title={p("kpiTopOrigin")}
            value={
              <TopTerminalKpiValue
                loading={loading}
                name={cards.topOrigin}
                qty={cards.topOriginQty}
                p={p}
                accent={COLORS.teal}
              />
            }
            color={COLORS.teal}
            helpText={p("kpiTopOriginHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_top_origin",
                  metricName: p("kpiTopOrigin"),
                  metricDefinition: p("kpiTopOriginHelp"),
                  metricValue: {
                    name: cards.topOrigin,
                    count: Number(cards.topOriginQty ?? 0),
                  },
                  metricHuman: formatTopTerminalLabel(cards.topOrigin, cards.topOriginQty, i18n.language),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<FlagIcon />}
            title={p("kpiTopDestination")}
            value={
              <TopTerminalKpiValue
                loading={loading}
                name={cards.topDestination}
                qty={cards.topDestinationQty}
                p={p}
                accent={COLORS.purple}
              />
            }
            color={COLORS.purple}
            helpText={p("kpiTopDestinationHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_top_destination",
                  metricName: p("kpiTopDestination"),
                  metricDefinition: p("kpiTopDestinationHelp"),
                  metricValue: {
                    name: cards.topDestination,
                    count: Number(cards.topDestinationQty ?? 0),
                  },
                  metricHuman: formatTopTerminalLabel(cards.topDestination, cards.topDestinationQty, i18n.language),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<ScheduleIcon />}
            title={p("kpiLeadTime")}
            value={
              loading
                ? "…"
                : formatAvgLeadTimeDisplay(cards.avgLeadTimeHours, p, i18n.language) || "—"
            }
            color={COLORS.indigo}
            helpText={p("kpiLeadTimeHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_avg_lead_time",
                  metricName: p("kpiLeadTime"),
                  metricDefinition: p("kpiLeadTimeHelp"),
                  metricValue: Number(cards.avgLeadTimeHours ?? 0),
                  metricHuman:
                    formatAvgLeadTimeDisplay(cards.avgLeadTimeHours, p, i18n.language) || "—",
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<PersonOutlineIcon />}
            title={p("kpiUniquePassengers")}
            value={loading ? "…" : String(cards.uniquePassengers ?? 0)}
            color={COLORS.green}
            helpText={p("kpiUniquePassengersHelp")}
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_unique_passengers",
                  metricName: p("kpiUniquePassengers"),
                  metricDefinition: p("kpiUniquePassengersHelp"),
                  metricValue: Number(cards.uniquePassengers ?? 0),
                  metricHuman: String(cards.uniquePassengers ?? 0),
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <KpiCard
            icon={<EventSeatIcon />}
            title={p("kpiMultiSeatOrders")}
            value={loading ? "…" : String(cards.multiSeatOrders ?? 0)}
            color={COLORS.pink}
            helpText={
              !loading && cards.avgSeatsPerOrder != null && Number(cards.avgSeatsPerOrder) > 0
                ? p("kpiMultiSeatHelpWithAvg", { avg: Number(cards.avgSeatsPerOrder).toFixed(2) })
                : p("kpiMultiSeatHelp")
            }
            loading={loading}
            titleAction={
              <AiBtn
                cfg={{
                  metric: "passagens_multi_seat_orders",
                  metricName: p("kpiMultiSeatOrders"),
                  metricDefinition: p("kpiMultiSeatHelp"),
                  metricValue: {
                    multiSeatOrders: Number(cards.multiSeatOrders ?? 0),
                    avgSeatsPerOrder: Number(cards.avgSeatsPerOrder ?? 0) || null,
                  },
                  metricHuman: String(cards.multiSeatOrders ?? 0),
                }}
              />
            }
          />
        </Grid>
      </Grid>

      {meta?.funnelAvailable && funnel?.steps?.length ? (
        <Card sx={{ mb: 3, borderRadius: 2 }} variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                {p("funnelTitle")}
              </Typography>
              {!loading ? (
                <AiBtn
                  cfg={{
                    metric: "passagens_funnel",
                    metricName: p("funnelTitle"),
                    metricDefinition:
                      "Funil de conversão das etapas web até emissão (alcance e abandono entre etapas).",
                    metricValue: (funnel.steps || []).map((s) => ({
                      name: p(`funnelStep.${s.key}`),
                      value: Number(s.reached ?? 0),
                    })),
                    metricHuman: `${(funnel.steps || []).length} etapas`,
                    summaryContext: {
                      worstDrop: funnel.worstDrop,
                      worstDropStepIndex: funnel.worstDropStepIndex,
                    },
                  }}
                />
              ) : null}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {p("funnelSubtitle")}
            </Typography>
            <Box sx={{ width: "100%", height: 320, mb: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={(funnel.steps || []).map((s) => ({
                    label: p(`funnelStep.${s.key}`),
                    alcance: s.reached,
                  }))}
                  margin={{ top: 8, right: 12, left: 8, bottom: 48 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="alcance" name={p("funnelColReached")} fill="#1565C0" radius={[4, 4, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={paperContainerSx}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>{p("funnelColStep")}</TableCell>
                    <TableCell align="right" sx={headerCellSx}>
                      {p("funnelColReached")}
                    </TableCell>
                    <TableCell align="right" sx={headerCellSx}>
                      {p("funnelColDrop")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(funnel.steps || []).map((s, idx) => (
                    <TableRow key={s.key} hover sx={bodyRowHoverSx}>
                      <TableCell>{p(`funnelStep.${s.key}`)}</TableCell>
                      <TableCell align="right" sx={numCellSx}>
                        {String(s.reached ?? 0)}
                      </TableCell>
                      <TableCell align="right" sx={numCellSx}>
                        {funnel.dropBetween && funnel.dropBetween[idx] != null
                          ? String(funnel.dropBetween[idx])
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {funnel.worstDropStepIndex > 0 && funnel.worstDrop > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {p("funnelWorstDrop")}: {p(`funnelStep.${funnel.steps[funnel.worstDropStepIndex - 1]?.key}`)} →{" "}
                {p(`funnelStep.${funnel.steps[funnel.worstDropStepIndex]?.key}`)} (−{funnel.worstDrop})
              </Typography>
            ) : null}
            {Array.isArray(funnel.cumulative) && funnel.cumulative.length ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {p("funnelCumulativeTitle")}
                </Typography>
                <Typography variant="body2" component="div">
                  {(funnel.cumulative || []).map((n, i) => {
                    const sk = funnel.steps[i]?.key;
                    if (!sk) return null;
                    return (
                      <span key={sk}>
                        {i > 0 ? " · " : ""}
                        {p(`funnelStep.${sk}`)}: {n}
                      </span>
                    );
                  })}
                </Typography>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">
                  {p("soldTableTitle")}
                </Typography>
                <Button component={RouterLink} to="/passagens" size="small" variant="text">
                  {p("viewAllPassagens", { defaultValue: "Ver todas" })}
                </Button>
              </Stack>
              <TableContainer component={Paper} variant="outlined" sx={paperContainerSx}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeaderCellSx}>{p("soldColDate")}</TableCell>
                      <TableCell sx={stickyHeaderCellSx}>{p("soldColClient")}</TableCell>
                      <TableCell sx={stickyHeaderCellSx}>{p("soldColRoute")}</TableCell>
                      <TableCell sx={stickyHeaderCellSx}>{p("soldColStatus")}</TableCell>
                      <TableCell align="right" sx={stickyHeaderCellSx}>
                        {p("soldColValue")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {soldTableLoading ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Skeleton height={28} />
                        </TableCell>
                      </TableRow>
                    ) : (soldTableData?.rows || []).length ? (
                      soldTableData.rows.map((row) => (
                        <TableRow key={row.messageId} hover sx={bodyRowHoverSx}>
                          <TableCell sx={numCellSx}>
                            {row.soldAt ? new Date(row.soldAt).toLocaleString("pt-BR") : "—"}
                          </TableCell>
                          <TableCell>{row.clientName || "—"}</TableCell>
                          <TableCell>
                            {[row.origem, row.destino].filter(Boolean).join(" → ") || "—"}
                          </TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell align="right" sx={numCellSx}>
                            {BRL(row.valor)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            {p("soldTableEmpty")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {p("abandonedTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {p("abandonedSubtitle")}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={paperContainerSx}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeaderCellSx}>{p("abandonedColClient")}</TableCell>
                      <TableCell sx={stickyHeaderCellSx}>{p("abandonedColRoute")}</TableCell>
                      <TableCell sx={stickyHeaderCellSx}>{p("abandonedColSource")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {abandonedLoading ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Skeleton height={28} />
                        </TableCell>
                      </TableRow>
                    ) : (abandonedData?.rows || []).length ? (
                      abandonedData.rows.map((row) => (
                        <TableRow key={row.leadId} hover sx={bodyRowHoverSx}>
                          <TableCell>{row.clientName || `#${row.leadId}`}</TableCell>
                          <TableCell>{row.route || "—"}</TableCell>
                          <TableCell>{row.lastSource || "—"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            {p("abandonedEmpty")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Vendas por dia da semana
                </Typography>
                {!loading && weekdaySeries.length ? (
                  <AiBtn
                    cfg={{
                      metric: "passagens_weekday_series",
                      metricName: "Vendas por dia da semana",
                      metricDefinition:
                        "Distribuição de bilhetes e GMV por dia da semana no período.",
                      metricValue: weekdaySeries.map((r) => ({
                        name: weekdayLabel(r.weekday),
                        tickets: Number(r.tickets ?? 0),
                        gmv: Number(r.gmv ?? 0),
                      })),
                      metricHuman: `${weekdaySeries.length} dias`,
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ width: "100%", height: 280 }}>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={260} />
                ) : weekdaySeries.length === 0 ? (
                  <Typography color="text.secondary">{p("noData")}</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weekdaySeries.map((r) => ({ ...r, label: weekdayLabel(r.weekday) }))}
                      margin={{ top: 8, right: 12, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => BRL(v)} />
                      <RechartsTooltip formatter={(value, name) => (name === "GMV" ? BRL(value) : value)} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="tickets" name="Bilhetes" fill="#0B60B0" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="gmv" name="GMV" stroke="#E91E63" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Vendas por hora do dia
                </Typography>
                {!loading && hourSeries.length ? (
                  <AiBtn
                    cfg={{
                      metric: "passagens_hour_series",
                      metricName: "Vendas por hora do dia",
                      metricDefinition:
                        "Volume de bilhetes vendidos por faixa horária (ajuda a planejar pico de demanda).",
                      metricValue: hourSeries.map((r) => ({
                        name: `${String(r.hour).padStart(2, "0")}h`,
                        value: Number(r.tickets ?? 0),
                      })),
                      metricHuman: `${hourSeries.length} faixas`,
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ width: "100%", height: 280 }}>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={260} />
                ) : hourSeries.length === 0 ? (
                  <Typography color="text.secondary">{p("noData")}</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={hourSeries.map((r) => ({ ...r, label: `${String(r.hour).padStart(2, "0")}h` }))}
                      margin={{ top: 8, right: 12, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="tickets" name="Bilhetes" fill="#00838F" radius={[4, 4, 0, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3, borderRadius: 2 }} variant="outlined">
        <CardContent sx={{ pt: 1.5, pb: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
                {p("sourceByChannelTitle")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {!loading && sourceSeries.length ? (
                  <AiBtn
                    cfg={{
                      metric: "passagens_source_by_channel",
                      metricName: p("sourceByChannelTitle"),
                      metricDefinition: p("sourceByChannelHelp"),
                      metricValue: sourceSeries.map((r) => ({
                        name: sourceSeriesChannelLabel(r, p),
                        tickets: Number(r.tickets ?? 0),
                        orders: Number(r.orders ?? 0),
                      })),
                      metricHuman: `${sourceSeries.length} canais`,
                    }}
                  />
                ) : null}
              </Box>
            </Box>
            <Typography
              component="p"
              variant="caption"
              sx={(theme) => ({
                m: 0,
                color: theme.palette.text.secondary,
                opacity: 0.72,
                fontSize: "0.6875rem",
                fontWeight: 400,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              })}
            >
              {p("sourceByChannelHelp")}
            </Typography>
          </Box>
          {loading ? (
            <Skeleton variant="rounded" height={36} sx={{ borderRadius: 1 }} />
          ) : sourceSeries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {p("noData")}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={paperContainerSx}>
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, width: "48%" }}>{p("colChannel")}</TableCell>
                    <TableCell align="right" sx={{ ...headerCellSx, width: "26%", verticalAlign: "bottom" }}>
                      <Typography variant="inherit" component="span" sx={{ display: "block", fontWeight: 600 }}>
                        {p("tableColTickets")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 400, mt: 0.25, lineHeight: 1.2 }}>
                        {p("channelHeaderQtyHint")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ ...headerCellSx, width: "26%", verticalAlign: "bottom" }}>
                      <Typography variant="inherit" component="span" sx={{ display: "block", fontWeight: 600 }}>
                        {p("tableColOrders")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 400, mt: 0.25, lineHeight: 1.2 }}>
                        {p("channelHeaderQtyHint")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sourceSeries.map((r) => {
                    const tickets = Number(r.tickets ?? 0);
                    const orders = Number(r.orders ?? 0);
                    const loc = String(i18n.language || "pt-BR").replace(/_/g, "-");
                    return (
                      <TableRow key={String(r.originId)} hover sx={bodyRowHoverSx}>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                            {sourceSeriesChannelLabel(r, p)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ ...numCellSx, py: 1, fontWeight: 600, fontSize: "0.9375rem" }}>
                          {tickets.toLocaleString(loc)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...numCellSx, py: 1, fontWeight: 600, fontSize: "0.9375rem" }}>
                          {orders.toLocaleString(loc)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: "1 1 auto", minWidth: 0 }}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
            {p("geoTitle")}
          </Typography>
          {!loading && (origins.length || destinations.length) ? (
            <AiBtn
              cfg={{
                metric: "passagens_geo_overview",
                metricName: p("geoTitle"),
                metricDefinition:
                  "Resumo geográfico: principais origens e destinos por volume de bilhetes no período.",
                metricValue: {
                  origins: origins.slice(0, 12).map((row) => ({
                    name: row.name,
                    value: Number(row.qty ?? 0),
                  })),
                  destinations: destinations.slice(0, 12).map((row) => ({
                    name: row.name,
                    value: Number(row.qty ?? 0),
                  })),
                },
                metricHuman: `${origins.length} origens · ${destinations.length} destinos`,
              }}
            />
          ) : null}
        </Box>
        <ToggleButtonGroup
          size="small"
          value={geoFocus}
          exclusive
          onChange={(_, v) => v && setGeoFocus(v)}
          aria-label={p("geoToggleAria")}
        >
          <ToggleButton value="origins">{p("toggleOrigins")}</ToggleButton>
          <ToggleButton value="destinations">{p("toggleDestinations")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: geoFocus === "origins" ? "primary.main" : "transparent",
              transition: "border-color 0.2s ease",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: accent, flex: 1, fontWeight: 600 }}>
                  {p("tableOrigins")}
                </Typography>
                {!loading && origins.length ? (
                  <AiBtn
                    cfg={{
                      metric: "passagens_origins_table",
                      metricName: p("tableOrigins"),
                      metricDefinition:
                        "Ranking de terminais/cidades de origem por quantidade de bilhetes no período.",
                      metricValue: origins.map((row) => ({
                        name: row.name,
                        value: Number(row.qty ?? 0),
                      })),
                      metricHuman: `${origins.length} linhas`,
                    }}
                  />
                ) : null}
              </Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ ...paperContainerSx, maxHeight: 320 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeaderCellSx}>{p("colTerminal")}</TableCell>
                      <TableCell align="right" sx={stickyHeaderCellSx}>
                        {p("colQty")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Skeleton height={28} />
                        </TableCell>
                      </TableRow>
                    ) : origins.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            {p("noData")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      origins.map((row) => (
                        <TableRow key={row.name} hover sx={bodyRowHoverSx}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right" sx={numCellSx}>
                            {row.qty}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: geoFocus === "destinations" ? "primary.main" : "transparent",
              transition: "border-color 0.2s ease",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: accent, flex: 1, fontWeight: 600 }}>
                  {p("tableDestinations")}
                </Typography>
                {!loading && destinations.length ? (
                  <AiBtn
                    cfg={{
                      metric: "passagens_destinations_table",
                      metricName: p("tableDestinations"),
                      metricDefinition:
                        "Ranking de terminais/cidades de destino por quantidade de bilhetes no período.",
                      metricValue: destinations.map((row) => ({
                        name: row.name,
                        value: Number(row.qty ?? 0),
                      })),
                      metricHuman: `${destinations.length} linhas`,
                    }}
                  />
                ) : null}
              </Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ ...paperContainerSx, maxHeight: 320 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeaderCellSx}>{p("colTerminal")}</TableCell>
                      <TableCell align="right" sx={stickyHeaderCellSx}>
                        {p("colQty")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Skeleton height={28} />
                        </TableCell>
                      </TableRow>
                    ) : destinations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            {p("noData")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      destinations.map((row) => (
                        <TableRow key={row.name} hover sx={bodyRowHoverSx}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right" sx={numCellSx}>
                            {row.qty}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {p("topRoutesTitle")}
            </Typography>
            {!loading && topRoutes.length ? (
              <AiBtn
                cfg={{
                  metric: "passagens_top_routes",
                  metricName: p("topRoutesTitle"),
                  metricDefinition:
                    "Rotas (origem → destino) mais vendidas no período e respectivas quantidades.",
                  metricValue: topRoutes.map((row) => ({
                    name: row.route,
                    value: Number(row.qty ?? 0),
                  })),
                  metricHuman: `${topRoutes.length} rotas`,
                }}
              />
            ) : null}
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={paperContainerSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>{p("colRoute")}</TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {p("colQty")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ) : topRoutes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        {p("noData")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  topRoutes.map((row) => (
                    <TableRow key={row.route} hover sx={bodyRowHoverSx}>
                      <TableCell>{row.route}</TableCell>
                      <TableCell align="right" sx={numCellSx}>
                        {row.qty}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6">{p("chartDailyTitle")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {p("chartDailySubtitle")}
              </Typography>
            </Box>
            {!loading && dailySeries.length ? (
              <AiBtn
                cfg={{
                  metric: "passagens_daily_series",
                  metricName: p("chartDailyTitle"),
                  metricDefinition:
                    "Série diária de bilhetes e GMV ao longo do período selecionado.",
                  metricValue: dailySeries.map((r) => ({
                    day: r.date,
                    tickets: Number(r.tickets ?? 0),
                    gmv: Number(r.gmv ?? 0),
                  })),
                  metricHuman: `${dailySeries.length} dias`,
                }}
              />
            ) : null}
          </Box>
          <Box sx={{ width: "100%", height: 380 }}>
            {loading ? (
              <Skeleton variant="rectangular" width="100%" height={360} />
            ) : dailySeries.length === 0 ? (
              <Typography color="text.secondary">{p("noData")}</Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dailySeries} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatIsoDateAxisBr}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => BRL(v)} />
                  <RechartsTooltip
                    labelFormatter={formatIsoDateAxisBr}
                    formatter={(value, name) =>
                      name === p("chartGmvSeries") ? BRL(value) : value
                    }
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="tickets" name={p("chartTicketsSeries")} fill="#0B60B0" radius={[4, 4, 0, 0]} />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="gmv"
                    name={p("chartGmvSeries")}
                    stroke="#E91E63"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start, end }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="venda-passagens"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Box>
  );
};

export default VendaPassagens;
